/**
 * IA 42 — the trust-graduation prompt ([ADR-041](../../docs/platform/11-technical-architecture.md#8--architecture-decision-records)).
 *
 * ── What ADR-041 actually requires, clause by clause ──────────────────────────────────────────
 *
 * *"When a PWA session hits a real ceiling … the UI **quantifies the specific value being missed**
 * ('50,000 SharePoint documents could be audited for staleness — this needs cloud compute') and offers
 * the Managed-Cloud or BYO-Cloud upgrade path, including the architecture summary an IT/Security
 * reviewer will ask for — **never a bare 'feature disabled' error**."*
 *
 * The rationale is the part worth keeping in view: *"a buyer who fought for local-only approval and
 * then hits a wall feels misled; reframing the ceiling as a quantified next step turns a technical
 * limitation into the internal champion's own sales-enablement artifact."* So this component is not an
 * upsell — it is a document that person forwards to their own security reviewer.
 *
 * ── Three design consequences of that, which are the whole build ───────────────────────────────
 *
 * ⚠️ **It refuses to render without a QUANTITY.** `missing` is required and typed as a sentence with a
 * number in it, because "some features need cloud" is exactly the bare-disabled message ADR-041
 * forbids. A caller that cannot say how much is being missed has no business opening this.
 *
 * ⚠️ **It offers BOTH tiers, and says what each one costs in trust.** Managed Cloud means Dedupr holds
 * the data; BYO-Cloud means it never leaves the customer's own VPC. Offering only the first would make
 * the prompt a sales pitch to somebody whose whole reason for being here was not trusting a vendor
 * with their data — and the second is precisely the answer for that person.
 *
 * ⚠️ **It carries the architecture summary inline, not behind a link.** The reviewer this is forwarded
 * to will not click through. Region pinning, human approval, snapshot-and-undo, and the audit chain
 * are the four things asked in every security review, so they are in the artifact itself.
 *
 * ── And what it deliberately is NOT ───────────────────────────────────────────────────────────
 *
 * ⚠️ **Not a navigation node** — [08 §4](../../docs/platform/08-information-architecture.md): *"a
 * nagging upgrade menu item is not the same thing as a contextual, quantified prompt at the moment of
 * friction."* It opens where the ceiling was hit and nowhere else.
 *
 * ⚠️ **Not a blocker.** Dismiss returns you to what you were doing, which still works up to the
 * ceiling. A modal that has to be resolved before continuing would be the wall this exists to replace.
 */
import { Button, Callout, DefinitionList, Modal } from '@dedupr/ui';
import './GraduationPrompt.css';

export interface GraduationPromptProps {
  /** What was reached. Shown as the title, so it reads as a fact rather than a failure. */
  ceiling: string;
  /**
   * ⚠️ **Required, and it must contain a quantity.** ADR-041's whole point: name the specific value
   * being missed. Examples the ADR itself gives — *"50,000 SharePoint documents could be audited for
   * staleness"*, *"the 12,000 records past this file's limit are not being compared"*.
   */
  missing: string;
  /**
   * Why the device cannot do it — one sentence, mechanical rather than commercial.
   *
   * ⚠️ Separate from `missing` on purpose: a person forwarding this needs the *reason* to survive being
   * quoted without the number, and vice versa.
   */
  because: string;
  onDismiss: () => void;
  /**
   * Where "talk to us" goes.
   *
   * ⚠️ **Defaulted, and it was not.** It was optional with the control omitted when absent — and
   * **neither caller passed it**, so the primary action never rendered and this modal's only control
   * was "keep working on this device". A graduation prompt whose sole option is to carry on as you
   * were is not the journey [ADR-041](../../../docs/platform/11-technical-architecture.md#8--architecture-decision-records)
   * asks for; it is a wall with a polite sentence in front of it. Found in a browser, which is the
   * only place an *absent optional prop* is visible at all — nine tests covered the wording, both
   * tiers, the security summary and the dismiss path, and none of them could see that the reader has
   * nowhere to go.
   */
  contactHref?: string;
}

/**
 * The default destination.
 *
 * ⚠️ `mailto:`, not an in-app form, and deliberately: the reader here is on the **Entry/Sandbox**
 * tier and may have no organisation at all, so a route that needs a session is a route half of them
 * cannot use. It matches the landing page's own fallback address rather than inventing a second one.
 */
export const GRADUATION_CONTACT = 'mailto:hello@dedupr.cloud?subject=Moving%20beyond%20on-device%20processing';

export function GraduationPrompt({
  ceiling,
  missing,
  because,
  onDismiss,
  contactHref = GRADUATION_CONTACT,
}: GraduationPromptProps) {
  return (
    <Modal
      open
      onOpenChange={(next) => {
        if (!next) onDismiss();
      }}
      eyebrow="Processing mode"
      title={ceiling}
      /* `lg` — the prototype's width for "an explainer with side-by-side options", which is exactly
         what the two-tier comparison below is. Width signals how much a modal is about to ask. */
      width="lg"
      actions={
        <>
          {/* ⚠️ Dismiss, not "cancel" — nothing was attempted, so there is nothing to cancel. And it
              returns to work that still functions, which is what stops this being a wall. */}
          <Button variant="secondary" onClick={onDismiss}>
            Keep working on this device
          </Button>
          {contactHref ? (
            <Button variant="primary" onClick={() => window.location.assign(contactHref)}>
              Talk to us about moving up
            </Button>
          ) : null}
        </>
      }
    >
      <div className="grad">
        {/* ⚠️ **The quantity leads.** Everything else on this screen is context for this sentence, and
            ADR-041's rationale rests on it being specific: a champion cannot take "we hit a limit" to
            their own management, and can take a number. */}
        <Callout tone="info" title="What this device cannot reach">
          <p className="grad__missing">{missing}</p>
          <p>{because}</p>
        </Callout>

        <p className="grad__lede">
          Nothing you have done is lost, and everything already on this device still works. This is
          where the on-device tier stops — not where Dedupr does.
        </p>

        <h3 className="grad__heading">Two ways past it, and they differ in who holds the data</h3>
        <DefinitionList
          items={[
            {
              term: 'Managed Cloud',
              value: 'Dedupr runs it',
              hint:
                'Your records are staged and processed on Dedupr’s infrastructure, pinned to the region you choose and never moved out of it. Fastest to start — there is nothing to deploy.',
            },
            {
              term: 'BYO-Cloud',
              value: 'Your own cloud, Enterprise',
              /* ⚠️ Stated as plainly as the other, because this is the option for the person who is
                 here *because* they would not hand data to a vendor. Burying it would be answering
                 their objection with the thing they already declined. */
              hint:
                'The same engine runs inside your own AWS, GCP or Azure account. No record content ever reaches Dedupr, and nothing opens an inbound firewall rule — the deployed engine polls for work rather than being called.',
            },
          ]}
        />

        {/* ⚠️ **Inline, not behind a link.** This section exists for a reviewer who is reading a
            forwarded screenshot or a pasted quote, and will not click through to a docs page. */}
        <h3 className="grad__heading">What holds in every tier, including both of these</h3>
        <DefinitionList
          items={[
            {
              term: 'Region',
              value: 'Pinned, and immutable',
              hint: 'Chosen once and enforced in the database — records are never processed or staged outside it.',
            },
            {
              term: 'Approval',
              value: 'Every change is approved by a person',
              hint: 'Nothing is written to a connected system without somebody approving that specific change first.',
            },
            {
              term: 'Undo',
              value: 'Snapshot before write, 30 days',
              hint: 'The prior state is captured before anything changes, so an applied change can be reversed for at least thirty days.',
            },
            {
              term: 'Audit',
              value: 'Hash-chained, and externally timestamped',
              /* The one line that is *better* in cloud than on this device — see LocalAuditLog's own
                 note. Naming it here is the honest version of an upgrade argument. */
              hint:
                'Every change is recorded in a chain where altering an entry is detectable, and each day’s chain head is registered with an independent timestamping authority — which a device cannot do for itself.',
            },
          ]}
        />
      </div>
    </Modal>
  );
}
